<template>
  <canvas ref="canvas" class="cyber-grid-bg"></canvas>
</template>

<script>
/**
 * CyberGridBackground — 全屏底层赛博透视网格背景
 *
 * 深色背景上淡淡的青蓝色透视网格线，缓慢轻微呼吸缩放
 * 带微弱辉光，透明度很低，营造空间纵深感
 * requestAnimationFrame 驱动，resize 自适应
 */
export default {
  name: 'CyberGridBackground',
  data () {
    return {
      rafId: null,
      w: 0,
      h: 0,
      dpr: 1,
      resizeHandler: null,
      // 动画参数
      time: 0,
      // 网格配置
      gridSize: 60,      // 基础格子大小
      vanishX: 0,        // 透视消失点 X
      vanishY: 0         // 透视消失点 Y
    }
  },
  mounted () {
    this.resize()
    this.resizeHandler = () => this.resize()
    window.addEventListener('resize', this.resizeHandler)
    this.animate()
  },
  beforeUnmount () {
    if (this.rafId) cancelAnimationFrame(this.rafId)
    if (this.resizeHandler) window.removeEventListener('resize', this.resizeHandler)
  },
  methods: {
    resize () {
      this.dpr = Math.min(window.devicePixelRatio || 1, 2)
      this.w = window.innerWidth
      this.h = window.innerHeight
      const c = this.$refs.canvas
      if (!c) return
      c.width = this.w * this.dpr
      c.height = this.h * this.dpr
      c.style.width = this.w + 'px'
      c.style.height = this.h + 'px'
      const ctx = c.getContext('2d')
      ctx.scale(this.dpr, this.dpr)
      // 透视消失点：屏幕中下方偏上
      this.vanishX = this.w / 2
      this.vanishY = this.h * 0.35
    },
    animate () {
      this.rafId = requestAnimationFrame(this.animate)
      const c = this.$refs.canvas
      if (!c) return
      const ctx = c.getContext('2d')
      ctx.clearRect(0, 0, this.w, this.h)

      this.time += 0.005

      // 呼吸：透明度极缓慢起伏（不改变网格间距，避免透视跳变）
      const breathAlpha = 0.9 + 0.1 * Math.sin(this.time * 0.4)
      const vx = this.vanishX
      const vy = this.vanishY

      // ── 横线（透视收敛到消失点） ──
      const numHLines = 30
      for (let i = 1; i <= numHLines; i++) {
        // 从消失点向下扩展，距离越远格子越大（透视）
        const t = i / numHLines
        const easedT = t * t // 非线性：近处密远处疏
        const y = vy + easedT * (this.h - vy) * 1.2
        if (y > this.h + 20) break

        // 透明度随距离递增（远处更淡）
        const alpha = (0.03 + 0.06 * (1 - t * 0.6)) * breathAlpha
        ctx.strokeStyle = `rgba(80, 200, 220, ${alpha})`
        ctx.lineWidth = 0.6
        ctx.shadowColor = 'rgba(80, 200, 220, 0.3)'
        ctx.shadowBlur = 3
        ctx.beginPath()
        ctx.moveTo(0, y)
        ctx.lineTo(this.w, y)
        ctx.stroke()
      }

      // ── 竖线（从消失点辐射） ──
      const numVLines = 24
      const spread = this.w * 1.5 // 底部展开宽度
      for (let i = -numVLines / 2; i <= numVLines / 2; i++) {
        const ratio = i / (numVLines / 2)
        // 底部 X 位置
        const bottomX = vx + ratio * spread
        // 从消失点到底部画线
        const alpha = 0.04 + 0.05 * (1 - Math.abs(ratio) * 0.5)
        ctx.strokeStyle = `rgba(80, 200, 220, ${alpha})`
        ctx.lineWidth = 0.6
        ctx.shadowColor = 'rgba(80, 200, 220, 0.25)'
        ctx.shadowBlur = 3
        ctx.beginPath()
        ctx.moveTo(vx, vy)
        ctx.lineTo(bottomX, this.h + 20)
        ctx.stroke()
      }

      // ── 上半区：淡水平线（非透视，营造层次） ──
      for (let i = 1; i <= 8; i++) {
        const y = (vy / 9) * i
        if (y >= vy) break
        const alpha = 0.025 + 0.02 * (i / 8)
        ctx.strokeStyle = `rgba(80, 200, 220, ${alpha})`
        ctx.lineWidth = 0.5
        ctx.shadowBlur = 2
        ctx.beginPath()
        ctx.moveTo(0, y)
        ctx.lineTo(this.w, y)
        ctx.stroke()
      }

      // ── 消失点微弱辉光 ──
      ctx.shadowBlur = 0
      const glowR = 120 + 20 * Math.sin(this.time * 0.5)
      const grad = ctx.createRadialGradient(vx, vy, 0, vx, vy, glowR)
      grad.addColorStop(0, 'rgba(80, 200, 220, 0.06)')
      grad.addColorStop(1, 'rgba(80, 200, 220, 0)')
      ctx.fillStyle = grad
      ctx.fillRect(vx - glowR, vy - glowR, glowR * 2, glowR * 2)

      // ── 地平线高光 ──
      ctx.strokeStyle = 'rgba(100, 220, 240, 0.08)'
      ctx.lineWidth = 1
      ctx.shadowColor = 'rgba(100, 220, 240, 0.4)'
      ctx.shadowBlur = 6
      ctx.beginPath()
      ctx.moveTo(0, vy)
      ctx.lineTo(this.w, vy)
      ctx.stroke()

      ctx.shadowBlur = 0
    }
  }
}
</script>

<style scoped>
.cyber-grid-bg {
  position: fixed;
  inset: 0;
  z-index: -1;
  pointer-events: none;
}
</style>
